import { useState } from "react";
import Marquee from "react-fast-marquee";
import { Handshake, Check, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl } from "../../lib/api";
import { useLang } from "../../i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import Chapter from "./Chapter";
import Reveal from "./Reveal";

export default function Sponsor({ sponsors, settings }) {
    const { t } = useLang();
    const [open, setOpen] = useState(false);
    const [inquiry, setInquiry] = useState({ name: "", contact: "", package: "bulanan", message: "" });
    const [sending, setSending] = useState(false);

    const submitInquiry = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/sponsor-inquiries", inquiry);
            toast.success(t.form_success);
            setInquiry({ name: "", contact: "", package: "bulanan", message: "" });
            setOpen(false);
        } catch {
            toast.error(t.form_error);
        } finally {
            setSending(false);
        }
    };
    const packages = [
        { name: t.sponsor_monthly, key: "bulanan", tagline: t.pkg_monthly_tag, benefits: t.pkg_monthly_benefits },
        { name: t.sponsor_yearly, key: "tahunan", tagline: t.pkg_yearly_tag, benefits: t.pkg_yearly_benefits },
    ];
    return (
        <section id="sponsor" className="py-24 md:py-32 border-t border-racing-line bg-[#FBF9F5]" data-testid="sponsor-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <Chapter number="09" title={t.ch_sponsor_t} subtitle={t.ch_sponsor_s} />
            </div>

            {sponsors.length > 0 && (
                <Reveal className="mb-20 md:mb-24">
                    <div className="border-y border-racing-line py-8" data-testid="sponsor-marquee">
                        <Marquee speed={25} gradient={false} pauseOnHover>
                            {sponsors.map((s) => (
                                <div key={s.id} className="mx-10 flex items-center gap-4">
                                    {s.logo_url ? (
                                        <img src={fileUrl(s.logo_url)} alt={s.name} className="h-10 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-500" />
                                    ) : (
                                        <Handshake size={28} weight="duotone" className="text-racing-red" />
                                    )}
                                    <span className="font-display uppercase text-2xl md:text-3xl text-neutral-900/60 whitespace-nowrap">
                                        {s.name}
                                    </span>
                                    <span className="ml-2 text-[10px] uppercase tracking-[0.25em] text-racing-muted border border-racing-line px-2 py-1">
                                        {s.package}
                                    </span>
                                </div>
                            ))}
                        </Marquee>
                    </div>
                </Reveal>
            )}

            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <Reveal className="max-w-3xl">
                    <h3 className="font-display uppercase text-3xl md:text-5xl text-neutral-900 leading-[1.05]" data-testid="sponsor-headline">
                        {t.sponsor_headline}
                    </h3>
                    <p className="mt-6 text-base font-light text-racing-muted leading-relaxed">
                        {t.sponsor_desc}
                    </p>
                    <p className="mt-6 text-lg md:text-xl font-medium text-racing-red leading-relaxed">
                        {t.sponsor_tagline}
                    </p>
                    <p className="mt-4 text-base font-light text-neutral-900/80 leading-relaxed">
                        {t.sponsor_closing}
                    </p>
                </Reveal>

                <Reveal delay={0.1} className="mt-10">
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        data-testid="sponsor-interest-button"
                        className="inline-flex items-center gap-3 bg-racing-red hover:bg-racing-redhover text-white text-xs uppercase tracking-[0.25em] px-10 py-5 transition-colors duration-300"
                    >
                        <Handshake size={20} weight="bold" />
                        {t.sponsor_interest}
                    </button>
                </Reveal>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="sponsor-interest-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase tracking-wide text-2xl">{t.sponsor_interest}</DialogTitle>
                    </DialogHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-racing-line border border-racing-line mt-4">
                        {packages.map((p) => (
                            <div
                                key={p.key}
                                data-testid={`sponsor-package-${p.key}`}
                                className="h-full bg-racing-surface p-8"
                            >
                                <p className="text-xs uppercase tracking-[0.25em] text-racing-red">{p.tagline}</p>
                                <h3 className="mt-4 font-display uppercase text-2xl md:text-3xl text-neutral-900">{p.name}</h3>
                                <ul className="mt-6 space-y-3">
                                    {p.benefits.map((b) => (
                                        <li key={b} className="flex items-start gap-3 text-sm font-light text-neutral-900/80">
                                            <Check size={18} weight="bold" className="mt-0.5 shrink-0 text-racing-red" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href={settings.whatsapp || "#gabung"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`sponsor-cta-${p.key}`}
                                    className="mt-8 inline-flex items-center gap-3 border border-racing-red text-racing-red hover:bg-racing-red hover:text-white text-xs uppercase tracking-[0.25em] px-6 py-3.5 transition-colors duration-300"
                                >
                                    <WhatsappLogo size={18} weight="fill" />
                                    {t.sponsor_cta} {p.name}
                                </a>
                            </div>
                        ))}
                    </div>

                    <div className="border border-racing-line bg-white mt-8" data-testid="sponsor-form">
                        <div className="p-6 md:p-8">
                            <h3 className="font-display uppercase text-xl md:text-2xl text-neutral-900">{t.sponsor_form_t}</h3>
                            <p className="mt-2 text-sm font-light text-racing-muted">{t.sponsor_form_desc}</p>
                            <form onSubmit={submitInquiry} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="sponsor-inquiry-form">
                                <input
                                    required
                                    value={inquiry.name}
                                    onChange={(e) => setInquiry({ ...inquiry, name: e.target.value })}
                                    placeholder={t.form_name}
                                    data-testid="sponsor-form-name"
                                    className="bg-white border border-racing-line px-4 py-3.5 text-sm text-neutral-900 focus:outline-none focus:border-racing-red transition-colors"
                                />
                                <input
                                    required
                                    value={inquiry.contact}
                                    onChange={(e) => setInquiry({ ...inquiry, contact: e.target.value })}
                                    placeholder={t.form_contact}
                                    data-testid="sponsor-form-contact"
                                    className="bg-white border border-racing-line px-4 py-3.5 text-sm text-neutral-900 focus:outline-none focus:border-racing-red transition-colors"
                                />
                                <select
                                    value={inquiry.package}
                                    onChange={(e) => setInquiry({ ...inquiry, package: e.target.value })}
                                    data-testid="sponsor-form-package"
                                    className="bg-white border border-racing-line px-4 py-3.5 text-sm text-neutral-900 focus:outline-none focus:border-racing-red transition-colors"
                                >
                                    <option value="bulanan">{t.sponsor_monthly}</option>
                                    <option value="tahunan">{t.sponsor_yearly}</option>
                                </select>
                                <textarea
                                    rows={1}
                                    value={inquiry.message}
                                    onChange={(e) => setInquiry({ ...inquiry, message: e.target.value })}
                                    placeholder={t.form_message}
                                    data-testid="sponsor-form-message"
                                    className="bg-white border border-racing-line px-4 py-3.5 text-sm text-neutral-900 focus:outline-none focus:border-racing-red transition-colors"
                                />
                                <button
                                    type="submit"
                                    disabled={sending}
                                    data-testid="sponsor-form-submit"
                                    className="md:col-span-2 bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-4 transition-colors duration-300"
                                >
                                    {sending ? t.form_sending : t.form_submit}
                                </button>
                            </form>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    );
}
